import { SidebarTrigger, useSidebar } from '@/components/ui/sidebar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { BookOpenCheck, Clock, CheckCircle2, Circle, ExternalLink } from 'lucide-react';
import SEO from '@/components/SEO';

const phases = [
  {
    name: 'Foundations',
    weeks: '1-6',
    progress: 75,
    modules: [
      { title: 'HTML & CSS Basics', provider: 'freeCodeCamp', hours: 10, status: 'completed', skills: ['HTML', 'CSS'] },
      { title: 'JavaScript Fundamentals', provider: 'JavaScript.info', hours: 20, status: 'in-progress', skills: ['JavaScript'] },
      { title: 'Git & GitHub', provider: 'GitHub Learning Lab', hours: 5, status: 'completed', skills: ['Git', 'GitHub'] },
      { title: 'Command Line Basics', provider: 'The Odin Project', hours: 3, status: 'not-started', skills: ['CLI'] },
    ]
  },
  {
    name: 'Intermediate',
    weeks: '7-14',
    progress: 30,
    modules: [
      { title: 'React Fundamentals', provider: 'React.dev', hours: 25, status: 'in-progress', skills: ['React', 'JSX'] },
      { title: 'TypeScript Basics', provider: 'TypeScript Handbook', hours: 15, status: 'not-started', skills: ['TypeScript'] },
      { title: 'REST APIs', provider: 'MDN Web Docs', hours: 10, status: 'not-started', skills: ['APIs', 'HTTP'] },
      { title: 'Database Fundamentals', provider: 'Khan Academy', hours: 12, status: 'not-started', skills: ['SQL', 'Databases'] },
    ]
  },
  {
    name: 'Job-Ready',
    weeks: '15-24',
    progress: 0,
    modules: [
      { title: 'Advanced React Patterns', provider: 'Epic React', hours: 30, status: 'not-started', skills: ['React', 'Patterns'] },
      { title: 'Testing & CI/CD', provider: 'Testing JavaScript', hours: 20, status: 'not-started', skills: ['Testing', 'CI/CD'] },
      { title: 'System Design Basics', provider: 'System Design Primer', hours: 15, status: 'not-started', skills: ['Architecture'] },
      { title: 'Interview Preparation', provider: 'LeetCode', hours: 40, status: 'not-started', skills: ['Algorithms', 'Interview'] },
    ]
  }
];

const getStatusBadge = (status: string) => {
  switch (status) {
    case 'completed':
      return <Badge variant="default">Completed</Badge>;
    case 'in-progress':
      return <Badge variant="secondary">In Progress</Badge>;
    default:
      return <Badge variant="outline">Not Started</Badge>;
  }
};

const getStatusIcon = (status: string) => {
  if (status === 'completed') {
    return <CheckCircle2 className="h-4 w-4 text-primary" />;
  }
  return <Circle className="h-4 w-4 text-muted-foreground" />;
};

export default function LearningPlan() {
  const { isMobile } = useSidebar();

  return (
    <div className="flex flex-col flex-1 overflow-y-auto p-4 md:p-6 lg:p-8 bg-background text-foreground space-y-6 pb-16">
      <SEO title="Learning Plan - VeteranOS" description="Your phased curriculum and learning path" />
      
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center border-b border-border pb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-primary">Learning Plan</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Sequenced curriculum with phases: Foundations → Intermediate → Job-Ready
          </p>
        </div>
        <div className="flex items-center gap-4 mt-4 md:mt-0">
          {isMobile && <SidebarTrigger />}
        </div>
      </div>

      {/* Phases */}
      <div className="space-y-8">
        {phases.map((phase, phaseIdx) => (
          <Card key={phaseIdx} accent className="transition-colors duration-200">
            <CardHeader>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <CardTitle className="text-xl flex items-center gap-2">
                    <BookOpenCheck className="h-5 w-5 text-primary" />
                    Phase {phaseIdx + 1}: {phase.name}
                  </CardTitle>
                  <CardDescription className="mt-1">
                    Weeks {phase.weeks}
                  </CardDescription>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-sm text-muted-foreground">Progress</span>
                  <span className="text-lg font-bold text-primary">{phase.progress}%</span>
                </div>
              </div>
              <Progress value={phase.progress} className="h-2 mt-4" />
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {phase.modules.map((module, moduleIdx) => (
                  <div key={moduleIdx}>
                    <div className="flex items-start gap-4 p-4 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
                      <div className="mt-1">
                        {getStatusIcon(module.status)}
                      </div>
                      <div className="flex-1 space-y-2">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <h3 className="font-semibold text-foreground">{module.title}</h3>
                            <p className="text-sm text-muted-foreground mt-1">
                              {module.provider}
                            </p>
                          </div>
                          {getStatusBadge(module.status)}
                        </div>
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {module.hours} hours
                          </span>
                          <Separator orientation="vertical" className="h-4" />
                          <div className="flex flex-wrap gap-1">
                            {module.skills.map((skill, skillIdx) => (
                              <Badge key={skillIdx} variant="branch-secondary" className="text-xs">
                                {skill}
                              </Badge>
                            ))}
                          </div>
                        </div>
                        <div className="flex gap-2 pt-2">
                          {module.status === 'not-started' && (
                            <Button size="sm" variant="outline">
                              Start Module
                            </Button>
                          )}
                          {module.status === 'in-progress' && (
                            <Button size="sm" variant="default">
                              Continue
                            </Button>
                          )}
                          <Button size="sm" variant="ghost">
                            <ExternalLink className="h-3 w-3 mr-1" />
                            View Resource
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
